import { useState } from "react";
import {
  CheckCircle2,
  XCircle,
  RotateCcw,
  Sparkles,
  BookOpen,
  ArrowRight,
  History,
} from "@/lib/icons";
import { Button } from "@/components/ui/button";
import { TOPIC_QUIZZES, type QuizQuestion } from "@/data/topicQuizzes";

interface Props {
  topicId: string;
}

/**
 * Per-topic formative check. Surfaces misconception-targeted feedback for every
 * answer (right or wrong), not just a score. No data leaves the device.
 */
type Phase = "answering" | "review";

export default function FormativeQuiz({ topicId }: Props) {
  const questions = TOPIC_QUIZZES[topicId];
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [previousAnswers, setPreviousAnswers] = useState<Record<string, number> | null>(null);
  const [phase, setPhase] = useState<Phase>("answering");
  const [attempt, setAttempt] = useState(1);

  if (!questions || questions.length === 0) return null;

  const correctCount = questions.reduce((acc, q) => {
    const idx = answers[q.id];
    if (idx === undefined) return acc;
    return acc + (q.options[idx]?.correct ? 1 : 0);
  }, 0);
  const answeredCount = Object.keys(answers).length;
  const allAnswered = answeredCount === questions.length;

  // Build the "Learned from this" summary from the just-completed attempt.
  const missedSummary = questions
    .map((q) => {
      const idx = answers[q.id];
      if (idx === undefined) return null;
      const chosen = q.options[idx];
      if (chosen.correct) return null;
      const correct = q.options.find((o) => o.correct);
      return { q, chosen, correct };
    })
    .filter((x): x is { q: QuizQuestion; chosen: typeof questions[number]["options"][number]; correct: typeof questions[number]["options"][number] | undefined } => x !== null);

  const handleRetry = () => {
    setPreviousAnswers(answers);
    setPhase("review");
  };

  const handleStartRetry = () => {
    setAnswers({});
    setPhase("answering");
    setAttempt((a) => a + 1);
  };

  const handleResetAll = () => {
    setAnswers({});
    setPreviousAnswers(null);
    setPhase("answering");
    setAttempt(1);
  };

  return (
    <div className="mt-3 pt-3 border-t border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-left group"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-primary" aria-hidden="true" />
          <span className="text-xs font-semibold text-foreground uppercase tracking-wide">
            Formative check
          </span>
          <span className="text-[10px] font-mono text-muted-foreground">
            {questions.length} question{questions.length === 1 ? "" : "s"}
          </span>
        </div>
        <span className="text-[10px] text-muted-foreground group-hover:text-foreground transition-colors">
          {open ? "Hide" : "Start"}
        </span>
      </button>

      {open && (
        <div className="mt-3 space-y-4">
          {phase === "review" ? (
            <ReviewSummary
              missed={missedSummary}
              total={questions.length}
              previousCorrect={correctCount}
              onContinue={handleStartRetry}
              onCancel={() => setPhase("answering")}
            />
          ) : (
            <>
              {attempt > 1 && (
                <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 flex items-center gap-2 text-xs text-foreground">
                  <History size={12} className="text-primary" aria-hidden="true" />
                  <span>
                    Attempt <span className="font-mono">{attempt}</span>. Your
                    previous picks are shown faintly so you can see what to change.
                  </span>
                </div>
              )}

              {questions.map((q, qi) => (
                <QuestionBlock
                  key={q.id}
                  q={q}
                  index={qi}
                  selected={answers[q.id]}
                  previous={previousAnswers?.[q.id]}
                  onSelect={(idx) =>
                    setAnswers((prev) => ({ ...prev, [q.id]: idx }))
                  }
                />
              ))}

              <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2">
                <div className="text-xs text-muted-foreground">
                  {allAnswered ? (
                    <>
                      <span className="font-semibold text-foreground">
                        {correctCount}/{questions.length}
                      </span>{" "}
                      correct.{" "}
                      {correctCount === questions.length
                        ? "Nice - you're ready to move on."
                        : "Tap Retry to review what you missed before trying again."}
                    </>
                  ) : (
                    <>
                      Answered {answeredCount}/{questions.length}. Each answer
                      reveals feedback, including common misconceptions.
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {allAnswered && correctCount < questions.length && (
                    <Button
                      size="sm"
                      className="h-7 text-xs gap-1"
                      onClick={handleRetry}
                    >
                      <BookOpen size={12} aria-hidden="true" />
                      Review & retry
                    </Button>
                  )}
                  {(answeredCount > 0 || previousAnswers) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={handleResetAll}
                    >
                      <RotateCcw size={12} className="mr-1" aria-hidden="true" />
                      Start over
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function QuestionBlock({
  q,
  index,
  selected,
  previous,
  onSelect,
}: {
  q: QuizQuestion;
  index: number;
  selected: number | undefined;
  previous?: number;
  onSelect: (idx: number) => void;
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm text-foreground">
        <span className="font-mono text-muted-foreground mr-1.5">
          Q{index + 1}.
        </span>
        {q.prompt}
      </p>
      <div className="space-y-1.5">
        {q.options.map((opt, idx) => {
          const isSelected = selected === idx;
          const isPrevious = previous === idx && !isSelected;
          const showState = isSelected;
          const isCorrect = !!opt.correct;
          return (
            <div key={idx}>
              <button
                type="button"
                onClick={() => onSelect(idx)}
                className={`w-full text-left rounded-md border px-2.5 py-1.5 text-xs transition-colors flex items-start gap-2 ${
                  showState
                    ? isCorrect
                      ? "border-primary/50 bg-primary/10 text-foreground"
                      : "border-amber-500/40 bg-amber-500/10 text-foreground"
                    : isPrevious
                    ? "border-dashed border-amber-500/30 bg-amber-500/5 text-muted-foreground"
                    : "border-border bg-background hover:bg-muted text-muted-foreground hover:text-foreground"
                }`}
                aria-pressed={isSelected}
              >
                {showState ? (
                  isCorrect ? (
                    <CheckCircle2
                      size={12}
                      className="text-primary mt-0.5 flex-shrink-0"
                      aria-hidden="true"
                    />
                  ) : (
                    <XCircle
                      size={12}
                      className="text-amber-500 mt-0.5 flex-shrink-0"
                      aria-hidden="true"
                    />
                  )
                ) : (
                  <span
                    className="mt-0.5 flex-shrink-0 inline-block w-3 h-3 rounded-full border border-border"
                    aria-hidden="true"
                  />
                )}
                <span className="flex-1">{opt.text}</span>
                {isPrevious && (
                  <span className="ml-2 text-[9px] uppercase tracking-wide font-mono text-amber-500/80">
                    last try
                  </span>
                )}
              </button>
              {showState && (
                <p
                  className={`mt-1 ml-5 text-[11px] leading-snug ${
                    isCorrect ? "text-primary/90" : "text-amber-500/90"
                  }`}
                >
                  {opt.feedback}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReviewSummary({
  missed,
  total,
  previousCorrect,
  onContinue,
  onCancel,
}: {
  missed: {
    q: QuizQuestion;
    chosen: QuizQuestion["options"][number];
    correct: QuizQuestion["options"][number] | undefined;
  }[];
  total: number;
  previousCorrect: number;
  onContinue: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <BookOpen size={14} className="text-primary" aria-hidden="true" />
        <span className="text-xs font-semibold uppercase tracking-wide text-foreground">
          Learned from this attempt
        </span>
        <span className="text-[10px] font-mono text-muted-foreground">
          {previousCorrect}/{total} last time
        </span>
      </div>

      {missed.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No misses to review - you can retake to reinforce.
        </p>
      ) : (
        <ol className="space-y-2.5">
          {missed.map(({ q, chosen, correct }) => (
            <li
              key={q.id}
              className="rounded border border-border bg-background p-2.5 text-xs space-y-1.5"
            >
              <p className="text-foreground">
                <span className="font-mono text-muted-foreground mr-1">
                  Q.
                </span>
                {q.prompt}
              </p>
              <p className="text-amber-500/90">
                <span className="font-semibold">You picked:</span> {chosen.text}
              </p>
              <p className="text-amber-500/80 leading-snug">{chosen.feedback}</p>
              {correct && (
                <p className="text-primary/90 leading-snug">
                  <span className="font-semibold">Correct answer:</span>{" "}
                  {correct.text}
                </p>
              )}
            </li>
          ))}
        </ol>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={onCancel}
        >
          Back
        </Button>
        <Button size="sm" className="h-7 text-xs gap-1" onClick={onContinue}>
          Retake quiz
          <ArrowRight size={12} aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}